/**
 * Client-side image resize utility
 *
 * Phase 5: Hono Migration - Frontend Client-Side Image Resize
 *
 * Replaces server-side sharp image processing with browser-based
 * Canvas API resizing for Cloudflare Workers compatibility.
 *
 * @module utils/imageResize
 */

/**
 * Default options for image resizing
 */
const DEFAULT_OPTIONS = {
  maxWidth: 800,
  maxHeight: 800,
  quality: 0.85,
  outputFormat: 'image/jpeg',
  preserveAspectRatio: true,
  fixOrientation: true,
};

/**
 * Read EXIF orientation from image file
 * Returns orientation value (1-8) or 1 if not found
 *
 * @param {File} file - The image file to read EXIF from
 * @returns {Promise<number>} - EXIF orientation value (1-8)
 */
async function getExifOrientation(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const view = new DataView(e.target.result);

      // Check for JPEG SOI marker
      if (view.getUint16(0, false) !== 0xFFD8) {
        resolve(1);
        return;
      }

      const length = view.byteLength;
      let offset = 2;

      while (offset < length) {
        if (view.getUint16(offset + 2, false) <= 8) {
          resolve(1);
          return;
        }

        const marker = view.getUint16(offset, false);
        offset += 2;

        // APP1 marker (EXIF)
        if (marker === 0xFFE1) {
          // Check for "Exif" string
          if (view.getUint32(offset + 2, false) !== 0x45786966) {
            resolve(1);
            return;
          }

          const little = view.getUint16(offset + 8, false) === 0x4949;
          offset += 8;

          const tags = view.getUint16(offset + 2, little);
          offset += 4;

          for (let i = 0; i < tags; i++) {
            // Orientation tag (0x0112)
            if (view.getUint16(offset + (i * 12), little) === 0x0112) {
              resolve(view.getUint16(offset + (i * 12) + 8, little));
              return;
            }
          }
        } else if ((marker & 0xFF00) !== 0xFF00) {
          break;
        } else {
          offset += view.getUint16(offset, false);
        }
      }

      resolve(1);
    };

    reader.onerror = () => resolve(1);

    // Only read first 64KB for EXIF data
    reader.readAsArrayBuffer(file.slice(0, 65536));
  });
}

/**
 * Apply EXIF orientation transformation to canvas context
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @param {number} orientation - EXIF orientation (1-8)
 */
function applyOrientation(ctx, width, height, orientation) {
  switch (orientation) {
    case 2:
      ctx.transform(-1, 0, 0, 1, width, 0);
      break;
    case 3:
      ctx.transform(-1, 0, 0, -1, width, height);
      break;
    case 4:
      ctx.transform(1, 0, 0, -1, 0, height);
      break;
    case 5:
      ctx.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      ctx.transform(0, 1, -1, 0, height, 0);
      break;
    case 7:
      ctx.transform(0, -1, -1, 0, height, width);
      break;
    case 8:
      ctx.transform(0, -1, 1, 0, 0, width);
      break;
    default:
      break;
  }
}

/**
 * Check if orientation requires swapping width and height
 *
 * @param {number} orientation - EXIF orientation (1-8)
 * @returns {boolean} - True if width/height should be swapped
 */
function shouldSwapDimensions(orientation) {
  return orientation >= 5 && orientation <= 8;
}

/**
 * Load an image from a File or Blob
 *
 * @param {File|Blob} file - The image file to load
 * @returns {Promise<HTMLImageElement>} - Loaded image element
 */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Calculate new dimensions while preserving aspect ratio
 *
 * @param {number} width - Original width
 * @param {number} height - Original height
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @returns {{ width: number, height: number }} - New dimensions
 */
function calculateDimensions(width, height, maxWidth, maxHeight) {
  // If image is smaller than max dimensions, return original
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  // Calculate scale factor
  const ratio = Math.min(maxWidth / width, maxHeight / height);

  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

/**
 * Resize an image file with optional orientation fix
 *
 * @param {File} file - The image file to resize
 * @param {Object} options - Resize options
 * @param {number} [options.maxWidth=800] - Maximum width
 * @param {number} [options.maxHeight=800] - Maximum height
 * @param {number} [options.quality=0.85] - JPEG quality (0-1)
 * @param {string} [options.outputFormat='image/jpeg'] - Output MIME type
 * @param {boolean} [options.preserveAspectRatio=true] - Maintain aspect ratio
 * @param {boolean} [options.fixOrientation=true] - Fix EXIF orientation
 * @returns {Promise<File>} - Resized image file
 */
export async function resizeImage(file, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { maxWidth, maxHeight, quality, outputFormat, fixOrientation } = opts;

  // Skip non-image files
  if (!file.type.startsWith('image/')) {
    return file;
  }

  // Skip GIFs (to preserve animation) unless explicitly requested
  if (file.type === 'image/gif' && !options.forceResize) {
    return file;
  }

  // Get EXIF orientation if enabled
  const orientation = fixOrientation ? await getExifOrientation(file) : 1;

  // Load the image
  const img = await loadImage(file);

  // Determine original dimensions (considering orientation)
  let origWidth = img.width;
  let origHeight = img.height;

  if (shouldSwapDimensions(orientation)) {
    [origWidth, origHeight] = [origHeight, origWidth];
  }

  // Calculate new dimensions
  const { width: newWidth, height: newHeight } = calculateDimensions(
    origWidth,
    origHeight,
    maxWidth,
    maxHeight
  );

  // Skip resize if image is already small enough and no orientation fix needed
  if (newWidth === origWidth && newHeight === origHeight && orientation === 1) {
    // If format is same, return original file
    if (file.type === outputFormat) {
      return file;
    }
  }

  // Create canvas and resize
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Handle canvas context creation failure (e.g., in some browsers/environments)
  if (!ctx) {
    console.warn('Failed to get canvas 2d context, returning original file');
    return file;
  }

  // Set canvas dimensions
  canvas.width = newWidth;
  canvas.height = newHeight;

  // Apply orientation transformation if needed
  if (orientation !== 1) {
    applyOrientation(ctx, newWidth, newHeight, orientation);

    // For rotated orientations (5-8), draw with swapped dimensions
    if (shouldSwapDimensions(orientation)) {
      ctx.drawImage(img, 0, 0, newHeight, newWidth);
    } else {
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
    }
  } else {
    ctx.drawImage(img, 0, 0, newWidth, newHeight);
  }

  // Convert to blob
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        // Handle toBlob failure (e.g., unsupported format)
        if (!blob) {
          console.warn('canvas.toBlob returned null, returning original file');
          resolve(file);
          return;
        }

        // Determine file extension
        let extension = '.jpg';
        if (outputFormat === 'image/png') {
          extension = '.png';
        } else if (outputFormat === 'image/webp') {
          extension = '.webp';
        }

        // Generate filename
        const baseName = file.name.replace(/\.[^/.]+$/, '');
        const newFileName = `${baseName}${extension}`;

        // Create new File object
        const resizedFile = new File([blob], newFileName, {
          type: outputFormat,
          lastModified: Date.now(),
        });

        resolve(resizedFile);
      },
      outputFormat,
      quality
    );
  });
}

/**
 * Resize image for profile pictures (square, smaller dimensions)
 *
 * @param {File} file - The image file to resize
 * @param {number} [size=400] - Target size (width and height)
 * @param {number} [quality=0.85] - JPEG quality (0-1)
 * @returns {Promise<File>} - Resized image file
 */
export async function resizeProfilePicture(file, size = 400, quality = 0.85) {
  return resizeImage(file, {
    maxWidth: size,
    maxHeight: size,
    quality,
    outputFormat: 'image/jpeg',
    fixOrientation: true,
  });
}

/**
 * Resize image for gallery uploads (larger dimensions)
 *
 * @param {File} file - The image file to resize
 * @param {Object} options - Resize options
 * @param {number} [options.maxWidth=1920] - Maximum width
 * @param {number} [options.maxHeight=1080] - Maximum height
 * @param {number} [options.quality=0.85] - JPEG quality (0-1)
 * @returns {Promise<File>} - Resized image file
 */
export async function resizeGalleryImage(file, options = {}) {
  return resizeImage(file, {
    maxWidth: options.maxWidth || 1920,
    maxHeight: options.maxHeight || 1080,
    quality: options.quality || 0.85,
    outputFormat: 'image/jpeg',
    fixOrientation: true,
  });
}

/**
 * Resize multiple images in parallel
 *
 * @param {File[]} files - Array of image files
 * @param {Object} options - Resize options (passed to resizeImage)
 * @returns {Promise<File[]>} - Array of resized files
 */
export async function resizeImages(files, options = {}) {
  const resizePromises = Array.from(files).map((file) =>
    resizeImage(file, options).catch((err) => {
      console.error(`Failed to resize ${file.name}:`, err);
      return file; // Return original file on error
    })
  );

  return Promise.all(resizePromises);
}

/**
 * Check if a file needs resizing based on dimensions
 *
 * @param {File} file - The image file to check
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @returns {Promise<boolean>} - True if file needs resizing
 */
export async function needsResize(file, maxWidth = 800, maxHeight = 800) {
  if (!file.type.startsWith('image/')) {
    return false;
  }

  try {
    const img = await loadImage(file);
    return img.width > maxWidth || img.height > maxHeight;
  } catch {
    return false;
  }
}

const imageResizeUtils = {
  resizeImage,
  resizeProfilePicture,
  resizeGalleryImage,
  resizeImages,
  needsResize,
};

export default imageResizeUtils;
